import Vue from 'vue'
import type Aeppic from './aeppic.js'
import { findClosestElement } from './utils/dom-traversal.js'

type Key = string // '<kind>:<name>'

/**
 * Public API bound to a component/element.
 * All operations are element-relative → deterministic scope resolution.
 */
export class SharedContextApiForComponents {
  private _unwatchers: Set<() => void> = new Set()
  private _released = false

  constructor(
    private _el: HTMLElement,
    private _ctx: SharedContext
  ) {}

  private assertAlive() {
    if (this._released) {
      throw new Error('SharedContextApiForComponents has been released')
    }
  }

  release() {
    if (this._released) return
    this._released = true

    this._unwatchers.forEach(unwatch => unwatch())
    this._unwatchers.clear()

    // break references for GC
    ;(this as any)._ctx = null
    ;(this as any)._el = null
  }

  // -------- set --------

  /**
   * Replace entire value (clears previous keys).
   * set('kind:name', value)
   * set('scopeName', 'kind:name', value)
   */
  set(kindColonName: string, value: any): any
  set(scope: string, kindColonName: string, value: any): any
  set(a: string, b: any, c?: any) {
    this.assertAlive()
    if (c === undefined) {
      return this._ctx.replace(this._el, a, b)
    } else {
      return this._ctx.replaceInNamedScope(this._el, a, b, c)
    }
  }

  /**
   * Patch (merge shallowly) into existing object.
   * patch('kind:name', partial)
   * patch('scopeName', 'kind:name', partial)
   */
  patch(kindColonName: string, partial: any): any
  patch(scope: string, kindColonName: string, partial: any): any
  patch(a: string, b: any, c?: any) {
    this.assertAlive()
    if (c === undefined) {
      return this._ctx.patch(this._el, a, b)
    } else {
      return this._ctx.patchInNamedScope(this._el, a, b, c)
    }
  }

  // -------- get / find --------

  get(kindColonName: string): any
  get(scope: string, kindColonName: string): any
  get(a: string, b?: string) {
    this.assertAlive()
    return b === undefined
      ? this._ctx.get(this._el, a)
      : this._ctx.getInNamedScope(this._el, a, b)
  }

  find = this.get.bind(this)

  /**
   * Snapshot (non-reactive list) of all values for a kind in the resolved scope.
   * (If you need reactivity, build a higher-level derived watcher.)
   */
  findAllOfKind(kind: string): any[]
  findAllOfKind(scope: string, kind: string): any[]
  findAllOfKind(a: string, b?: string) {
    this.assertAlive()
    return b === undefined
      ? this._ctx.findAllOfKind(this._el, a)
      : this._ctx.findAllOfKindInNamedScope(this._el, a, b)
  }

  // -------- watch --------

  /**
   * Deep watch of the shared object.
   * NOTE (Vue 2): for deep watches on mutable objects, oldVal/newVal are often the same reference.
   * If you need diffs, compute them yourself or emit patches.
   */
  watch(
    kindColonName: string,
    callback: (newVal: any, oldVal: any) => void
  ): () => void
  watch(
    scope: string,
    kindColonName: string,
    callback: (newVal: any, oldVal: any) => void
  ): () => void
  watch(a: string, b: any, c?: any) {
    this.assertAlive()

    const kindColonName = c === undefined ? a : b
    const callback = c === undefined ? b : c
    const scope = c === undefined ? undefined : a

    const unwatch =
      scope === undefined
        ? this._ctx.watch(this._el, kindColonName, callback)
        : this._ctx.watchInNamedScope(this._el, scope, kindColonName, callback)

    let called = false
    const safeUnwatch = () => {
      if (!called) {
        called = true
        unwatch()
        this._unwatchers.delete(safeUnwatch)
      }
    }

    this._unwatchers.add(safeUnwatch)
    return safeUnwatch
  }
}

/**
 * Core store.
 * - Scope identity = DOM Element
 * - Per-scope map of stable observables (never replaced)
 */
export class SharedContext {
  private scopeData: WeakMap<Element, Map<Key, any>> = new WeakMap()

  constructor(private _aeppic: Aeppic) {}

  public forElement(el: HTMLElement) {
    return new SharedContextApiForComponents(el, this)
  }

  // ---------- scope resolution ----------

  /**
   * Resolve nearest scope:
   * 1) closest [data-aeppic-sharedscope]
   * 2) app root fallback
   *
   * Why: deterministic, CSS-like shadowing; no global ambiguity.
   */
  private locateScopeElement(
    el: HTMLElement,
    { fallbackToGlobal = true } = {}
  ): HTMLElement | null {
    const local = findClosestElement(el, '[data-aeppic-sharedscope]') as HTMLElement | null
    if (local) return local

    if (!fallbackToGlobal) return null

    const root = findClosestElement(
      el,
      '[data-ae-version].ae-root, [data-ae-version]'
    ) as HTMLElement | null

    return root
  }

  /**
   * Find a named scope relative to an element (ancestor search only).
   * Why: avoids global querySelector ambiguity when multiple scopes share the same name.
   */
  private findNamedScopeUp(el: HTMLElement, scopeName: string): HTMLElement | null {
    let current: HTMLElement | null = el
    while (current) {
      if (current.dataset?.aeppicSharedscope === scopeName) {
        return current
      }
      current = current.parentElement
    }
    return null
  }

  // ---------- keys ----------

  /**
   * Strict parsing to avoid silent bugs.
   */
  private split(kindColonName: string): [string, string] {
    if (!kindColonName) {
      throw new Error('SharedContext key must not be empty')
    }
    const idx = kindColonName.indexOf(':')
    if (idx === -1) {
      return [kindColonName, 'default']
    }
    const kind = kindColonName.slice(0, idx)
    const name = kindColonName.slice(idx + 1)
    if (!kind) throw new Error('SharedContext key must have non-empty kind')
    if (!name) throw new Error('SharedContext key must have non-empty name')
    return [kind, name]
  }

  private makeKey(kind: string, name: string): Key {
    return `${kind}:${name}`
  }

  // ---------- storage ----------

  private getOrCreateScopeMap(scopeEl: HTMLElement): Map<Key, any> {
    if (!this.scopeData.has(scopeEl)) {
      this.scopeData.set(scopeEl, new Map())
    }
    return this.scopeData.get(scopeEl)!
  }

  /**
   * Always returns the same observable for (scope, key).
   * Why: reference stability is required for Vue reactivity across components.
   */
  private ensureObservable(scopeEl: HTMLElement, key: Key): any {
    const map = this.getOrCreateScopeMap(scopeEl)
    if (!map.has(key)) {
      map.set(key, Vue.observable({}))
    }
    return map.get(key)
  }

  /**
   * Replace semantics: clear then assign.
   * Why: explicit "set" = authoritative state.
   */
  private replaceValue(scopeEl: HTMLElement, key: Key, value: any) {
    const obs = this.ensureObservable(scopeEl, key)

    Object.keys(obs).forEach(k => Vue.delete(obs, k))

    if (value && typeof value === 'object') {
      Object.keys(value).forEach(k => Vue.set(obs, k, value[k]))
    }

    return obs
  }

  /**
   * Patch semantics: shallow merge.
   * Why: common for incremental UI updates.
   */
  private patchValue(scopeEl: HTMLElement, key: Key, partial: any) {
    const obs = this.ensureObservable(scopeEl, key)

    if (partial && typeof partial === 'object') {
      Object.keys(partial).forEach(k => Vue.set(obs, k, partial[k]))
    }

    return obs
  }

  // ---------- public API (element-relative) ----------

  public get(el: HTMLElement, kindColonName: string) {
    const scopeEl = this.locateScopeElement(el)
    if (!scopeEl) {
      // Element not in a resolvable scope yet → return local placeholder (NOT shared globally)
      // Why: scope is unknown; sharing would be incorrect.
      return Vue.observable({})
    }

    const [k, n] = this.split(kindColonName)
    const key = this.makeKey(k, n)

    return this.ensureObservable(scopeEl, key)
  }

  public replace(el: HTMLElement, kindColonName: string, value: any) {
    const scopeEl = this.locateScopeElement(el)
    if (!scopeEl) return undefined

    const [k, n] = this.split(kindColonName)
    const key = this.makeKey(k, n)

    return this.replaceValue(scopeEl, key, value)
  }

  public patch(el: HTMLElement, kindColonName: string, partial: any) {
    const scopeEl = this.locateScopeElement(el)
    if (!scopeEl) return undefined

    const [k, n] = this.split(kindColonName)
    const key = this.makeKey(k, n)

    return this.patchValue(scopeEl, key, partial)
  }

  public findAllOfKind(el: HTMLElement, kind: string) {
    const scopeEl = this.locateScopeElement(el)
    if (!scopeEl) return []

    const map = this.scopeData.get(scopeEl)
    if (!map) return []

    const prefix = `${kind}:`
    const out: any[] = []
    for (const [key, obs] of map) {
      if (key.startsWith(prefix)) out.push(obs)
    }
    return out
  }

  // ---------- named scope (still element-relative) ----------

  public getInNamedScope(el: HTMLElement, scope: string, kindColonName: string) {
    const scopeEl = scope === 'global'
      ? this.locateScopeElement(el, { fallbackToGlobal: true }) // root fallback
      : this.findNamedScopeUp(el, scope)

    if (!scopeEl) {
      // Same reasoning as get(): unknown scope → do not share globally
      return Vue.observable({})
    }

    const [k, n] = this.split(kindColonName)
    const key = this.makeKey(k, n)

    return this.ensureObservable(scopeEl, key)
  }

  public replaceInNamedScope(
    el: HTMLElement,
    scope: string,
    kindColonName: string,
    value: any
  ) {
    const scopeEl =
      scope === 'global'
        ? this.locateScopeElement(el, { fallbackToGlobal: true })
        : this.findNamedScopeUp(el, scope)

    if (!scopeEl) return undefined

    const [k, n] = this.split(kindColonName)
    const key = this.makeKey(k, n)

    return this.replaceValue(scopeEl, key, value)
  }

  public patchInNamedScope(
    el: HTMLElement,
    scope: string,
    kindColonName: string,
    partial: any
  ) {
    const scopeEl =
      scope === 'global'
        ? this.locateScopeElement(el, { fallbackToGlobal: true })
        : this.findNamedScopeUp(el, scope)

    if (!scopeEl) return undefined

    const [k, n] = this.split(kindColonName)
    const key = this.makeKey(k, n)

    return this.patchValue(scopeEl, key, partial)
  }

  public findAllOfKindInNamedScope(el: HTMLElement, scope: string, kind: string) {
    const scopeEl =
      scope === 'global'
        ? this.locateScopeElement(el, { fallbackToGlobal: true })
        : this.findNamedScopeUp(el, scope)

    if (!scopeEl) return []

    const map = this.scopeData.get(scopeEl)
    if (!map) return []

    const prefix = `${kind}:`
    const out: any[] = []
    for (const [key, obs] of map) {
      if (key.startsWith(prefix)) out.push(obs)
    }
    return out
  }

  // ---------- watch ----------

  public watch(
    el: HTMLElement,
    kindColonName: string,
    cb: (newVal: any, oldVal: any) => void
  ): () => void {
    const scopeEl = this.locateScopeElement(el)
    if (!scopeEl) {
      // No scope → no stable target to watch
      return () => {}
    }

    const [k, n] = this.split(kindColonName)
    const key = this.makeKey(k, n)
    const obs = this.ensureObservable(scopeEl, key)

    const unwatch = this._aeppic.Vue.$watch(
      () => obs,
      cb,
      { deep: true }
    )

    let called = false
    return () => {
      if (!called) {
        called = true
        unwatch()
      }
    }
  }

  public watchInNamedScope(
    el: HTMLElement,
    scope: string,
    kindColonName: string,
    cb: (newVal: any, oldVal: any) => void
  ): () => void {
    const scopeEl =
      scope === 'global'
        ? this.locateScopeElement(el, { fallbackToGlobal: true })
        : this.findNamedScopeUp(el, scope)

    if (!scopeEl) return () => {}

    const [k, n] = this.split(kindColonName)
    const key = this.makeKey(k, n)
    const obs = this.ensureObservable(scopeEl, key)

    const unwatch = this._aeppic.Vue.$watch(
      () => obs,
      cb,
      { deep: true }
    )

    let called = false
    return () => {
      if (!called) {
        called = true
        unwatch()
      }
    }
  }

  public destroy() {
    // no-op (watching uses global Vue instance)
  }
}
